import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ChevronDown, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CATEGORIES, PRIMARY_NAV, NAV_LINKS } from "@/lib/constants";

const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/articles?q=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/70" data-testid="main-navbar">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link to="/" className="px-3 py-2 text-sm font-medium text-navy/80 hover:text-electric transition-colors" data-testid="nav-home">
              Home
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-navy/80 hover:text-electric transition-colors outline-none" data-testid="nav-topics">
                Topics <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {CATEGORIES.map((c) => (
                  <DropdownMenuItem key={c} asChild data-testid={`topic-${c.replace(/\s|&/g, "-").toLowerCase()}`}>
                    <Link to={`/articles?category=${encodeURIComponent(c)}`}>{c}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {PRIMARY_NAV.map((l) => (
              <Link key={l.to} to={l.to}
                className="px-3 py-2 text-sm font-medium text-navy/80 hover:text-electric transition-colors"
                data-testid={`nav-${l.label.replace(/\s|&/g, "-").toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden xl:flex items-center relative">
              <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search insights..."
                className="h-9 w-44 pl-9 bg-white/70"
                data-testid="nav-search-input"
              />
            </form>
            <Button asChild className="hidden sm:inline-flex btn-electric rounded-full px-5" data-testid="nav-get-started">
              <Link to="/lead-generation">Get Started <ArrowRight className="h-4 w-4" /></Link>
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[88vw] sm:w-96 overflow-y-auto" data-testid="mobile-menu">
                <SheetHeader>
                  <SheetTitle><Logo /></SheetTitle>
                </SheetHeader>
                <form onSubmit={submitSearch} className="relative mt-6">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search insights..." className="pl-9" data-testid="mobile-search-input" />
                </form>
                <div className="mt-6 flex flex-col">
                  {NAV_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                      className="py-3 border-b border-border/60 text-navy font-medium hover:text-electric transition-colors"
                      data-testid={`mobile-nav-${l.label.replace(/\s|&/g, "-").toLowerCase()}`}>
                      {l.label}
                    </Link>
                  ))}
                </div>
                <Button asChild className="btn-electric w-full mt-6 rounded-full" onClick={() => setOpen(false)}>
                  <Link to="/lead-generation">Get Started</Link>
                </Button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
